import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Users, Snowflake, Wifi, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roomInfo = {
  'salle-tiare': { name: 'Salle Tiare', capacity: 12, price: '2 200' },
  'salle-hibiscus': { name: 'Salle Hibiscus', capacity: 12, price: '2 200' }
};

const pricingValues = {
  entreprises: { hourly: '2 200', halfDay: '8 500', fullDay: '13 500' },
  associations: { hourly: '1 650', halfDay: '6 375', fullDay: '10 125' },
  etudiants: { hourly: '1 320', halfDay: '5 100', fullDay: '8 100' }
};

const categoryToPackageKey = { entreprises: 'entreprise', associations: 'association', etudiants: 'etudiant' };
const formulaToPackageKey = { hourly: 'hourly', halfDay: 'halfday', fullDay: 'fullday' };

export const RoomBookingPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const room = roomInfo[roomId] || roomInfo['salle-tiare'];

  const [activeTab, setActiveTab] = useState('connexion');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Auth form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');

  // Booking form
  const [selectedDate, setSelectedDate] = useState(null);
  const [category, setCategory] = useState('');
  const [formula, setFormula] = useState('');
  const [bookingName, setBookingName] = useState('');
  const [bookingEmail, setBookingEmail] = useState('');
  const [bookingPhone, setBookingPhone] = useState('');
  const [bookingCompany, setBookingCompany] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const calculatePrice = () => {
    if (!category || !formula) return null;
    const cat = pricingValues[category];
    return cat ? cat[formula] : null;
  };

  const getPackageId = () => {
    if (!category || !formula) return null;
    const catKey = categoryToPackageKey[category];
    const fKey = formulaToPackageKey[formula];
    if (!catKey || !fKey) return null;
    return `room-${catKey}-${fKey}`;
  };

  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (payment === 'success' && sessionId) {
      setPaymentSuccess(true);
      setIsLoggedIn(true);
      axios.get(`${API}/stripe/status/${sessionId}`).then(res => {
        if (res.data?.payment_status === 'paid') {
          toast.success('Paiement confirmé ! Votre réservation est validée.');
        }
      }).catch(() => {});
      setSearchParams({}, { replace: true });
    } else if (payment === 'cancelled') {
      setIsLoggedIn(true);
      toast.error('Paiement annulé. Votre réservation est en attente.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data?.token) {
        setIsLoggedIn(true);
        setBookingEmail(email);
        toast.success('Connexion réussie !');
      }
    } catch {
      // Allow proceeding as guest
      setIsLoggedIn(true);
      setBookingEmail(email);
      toast.info('Continuez en tant qu\'invité');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPhone || !regPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    try {
      await axios.post(`${API}/auth/register`, {
        name: regName, email: regEmail, phone: regPhone, password: regPassword
      });
      setIsLoggedIn(true);
      setBookingName(regName);
      setBookingEmail(regEmail);
      setBookingPhone(regPhone);
      toast.success('Inscription réussie !');
    } catch {
      setIsLoggedIn(true);
      setBookingName(regName);
      setBookingEmail(regEmail);
      setBookingPhone(regPhone);
      toast.info('Continuez en tant qu\'invité');
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedDate || !category || !formula || !bookingName || !bookingEmail || !bookingPhone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    const packageId = getPackageId();
    if (!packageId) {
      toast.error('Combinaison de tarif invalide');
      return;
    }
    setSubmitting(true);
    try {
      const bookingRes = await axios.post(`${API}/bookings`, {
        name: bookingName, email: bookingEmail, phone: bookingPhone,
        company: bookingCompany, notes: bookingNotes,
        date: format(selectedDate, 'yyyy-MM-dd'),
        category, formula, price: calculatePrice(),
        room: room.name, status: 'pending_payment'
      });
      const bookingId = bookingRes.data?.id;

      const stripeRes = await axios.post(`${API}/stripe/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        customer_name: bookingName,
        customer_email: bookingEmail,
        customer_phone: bookingPhone,
        success_path: `/location-salles/reserver/${roomId}`,
        cancel_path: `/location-salles/reserver/${roomId}`,
        booking_id: bookingId,
        extra_metadata: { room: room.name, date: format(selectedDate, 'yyyy-MM-dd'), category, formula }
      });
      if (stripeRes.data?.url) {
        window.location.href = stripeRes.data.url;
      } else {
        toast.error('Erreur lors de la création du paiement.');
        setSubmitting(false);
      }
    } catch {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      setSubmitting(false);
    }
  };

  // Auth screen (not logged in)
  if (!isLoggedIn) {
    return (
      <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="room-booking-page">
        {/* Left: Teal gradient */}
        <div className="bg-gradient-to-br from-[#0D9488] to-[#0F766E] text-white p-10 lg:p-16 flex flex-col justify-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,0.05) 35px, rgba(255,255,255,0.05) 70px)'
            }} />
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-8">
              <Building2 className="w-6 h-6" />
              <span className="font-bold text-lg">AERH Formations</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">
              Votre plateforme de réservation professionnelle
            </h1>
            <p className="text-white/70 text-lg mb-12">
              Gérez vos salles, acceptez les paiements et synchronisez vos calendriers — tout en un.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <p className="text-2xl font-bold">2</p>
                <p className="text-white/70 text-sm">Salles équipées</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <p className="text-2xl font-bold">12 pers.</p>
                <p className="text-white/70 text-sm">Capacité</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <p className="text-2xl font-bold">100%</p>
                <p className="text-white/70 text-sm">Climatisation</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <p className="text-2xl font-bold">Oui</p>
                <p className="text-white/70 text-sm">Wi-Fi inclus</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Auth form */}
        <div className="bg-[#F8FAFC] flex items-center justify-center p-8 lg:p-16">
          <div className="w-full max-w-md">
            {/* Tabs */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-8" data-testid="auth-tabs">
              <button
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'connexion' ? 'bg-white shadow text-[#1F2937]' : 'text-gray-500'}`}
                onClick={() => setActiveTab('connexion')}
                data-testid="tab-connexion"
              >
                Connexion
              </button>
              <button
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'inscription' ? 'bg-white shadow text-[#1F2937]' : 'text-gray-500'}`}
                onClick={() => setActiveTab('inscription')}
                data-testid="tab-inscription"
              >
                Inscription
              </button>
            </div>

            {activeTab === 'connexion' ? (
              <form onSubmit={handleLogin}>
                <h2 className="text-2xl font-bold text-[#1F2937] mb-1">Bon retour !</h2>
                <p className="text-gray-500 mb-6">Connectez-vous à votre compte</p>
                <div className="space-y-4">
                  <div>
                    <Label>Email</Label>
                    <Input type="email" placeholder="vous@exemple.com" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="login-email" />
                  </div>
                  <div>
                    <Label>Mot de passe</Label>
                    <Input type="password" placeholder="********" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" data-testid="login-password" />
                  </div>
                  <Button type="submit" className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white py-5" data-testid="login-submit">
                    Se connecter
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleRegister}>
                <h2 className="text-2xl font-bold text-[#1F2937] mb-1">Créer un compte</h2>
                <p className="text-gray-500 mb-6">Inscrivez-vous pour réserver</p>
                <div className="space-y-4">
                  <div>
                    <Label>Nom complet</Label>
                    <Input placeholder="Votre nom" value={regName} onChange={(e) => setRegName(e.target.value)} className="mt-1" data-testid="register-name" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" placeholder="vous@exemple.com" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} className="mt-1" data-testid="register-email" />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input type="tel" placeholder="+689 87 XX XX XX" value={regPhone} onChange={(e) => setRegPhone(e.target.value)} className="mt-1" data-testid="register-phone" />
                  </div>
                  <div>
                    <Label>Mot de passe</Label>
                    <Input type="password" placeholder="********" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} className="mt-1" data-testid="register-password" />
                  </div>
                  <Button type="submit" className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white py-5" data-testid="register-submit">
                    S'inscrire
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Booking form (logged in)
  return (
    <div className="bg-[#F8FAFC] min-h-[60vh]" data-testid="room-booking-form-page">
      {paymentSuccess && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-4" data-testid="payment-success-banner">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <p className="text-emerald-800 font-medium">Paiement confirmé ! Votre réservation de {room.name} est validée.</p>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" data-testid="booking-title">
          Réserver — {room.name}
        </h1>
        <p className="text-gray-500 mb-8">{room.capacity} personnes max · {room.price} F/h</p>

        <form onSubmit={handleBooking} className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Type de structuré *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="booking-category">
                  <SelectValue placeholder="Sélectionnez" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entreprises">Entreprise</SelectItem>
                  <SelectItem value="associations">Association (-25%)</SelectItem>
                  <SelectItem value="etudiants">Étudiant / UPF (-40%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Formule *</Label>
              <Select value={formula} onValueChange={setFormula}>
                <SelectTrigger className="mt-1" data-testid="booking-formula">
                  <SelectValue placeholder="Sélectionnez" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">À l'heure</SelectItem>
                  <SelectItem value="halfDay">Demi-journée</SelectItem>
                  <SelectItem value="fullDay">Journée complète</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {calculatePrice() && (
            <div className="bg-teal-50 p-4 rounded-xl">
              <p className="text-sm text-teal-700 font-medium">Tarif estimé</p>
              <p className="text-2xl font-bold text-teal-800">{calculatePrice()} F CFP</p>
            </div>
          )}

          <div>
            <Label className="text-sm font-medium">Date souhaitée *</Label>
            <div className="mt-2 flex justify-center">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                locale={fr}
                disabled={(date) => isBefore(date, startOfDay(new Date())) || date.getDay() === 0}
                className="rounded-xl border"
                data-testid="booking-calendar"
              />
            </div>
            {selectedDate && (
              <p className="text-center mt-2 text-sm text-teal-700">
                {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
              </p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Nom complet *</Label>
              <Input value={bookingName} onChange={(e) => setBookingName(e.target.value)} placeholder="Votre nom" className="mt-1" required data-testid="booking-name" />
            </div>
            <div>
              <Label>Entreprise / Structure</Label>
              <Input value={bookingCompany} onChange={(e) => setBookingCompany(e.target.value)} placeholder="Nom de votre structure" className="mt-1" data-testid="booking-company" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Email *</Label>
              <Input type="email" value={bookingEmail} onChange={(e) => setBookingEmail(e.target.value)} placeholder="votre@email.com" className="mt-1" required data-testid="booking-email" />
            </div>
            <div>
              <Label>Téléphone *</Label>
              <Input type="tel" value={bookingPhone} onChange={(e) => setBookingPhone(e.target.value)} placeholder="+689 87 XX XX XX" className="mt-1" required data-testid="booking-phone" />
            </div>
          </div>
          <div>
            <Label>Notes / Besoins particuliers</Label>
            <Input value={bookingNotes} onChange={(e) => setBookingNotes(e.target.value)} placeholder="Précisez vos besoins..." className="mt-1" data-testid="booking-notes" />
          </div>

          <Button type="submit" className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white py-6 rounded-full" disabled={submitting} data-testid="booking-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirection vers le paiement...</> : 'Réserver et payer en ligne'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default RoomBookingPage;
